import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations, currentStateImages } from '../data/mock';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';

const CurrentStateGallery = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(currentStateImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % currentStateImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(currentStateImages[nextIndex]);
  };

  const goToPrev = () => {
    const prevIndex = (currentIndex - 1 + currentStateImages.length) % currentStateImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(currentStateImages[prevIndex]);
  };

  // Show only first 6 images in grid
  const displayImages = currentStateImages.slice(0, 6);

  return (
    <section id="current-state" className="py-24 lg:py-32 bg-stone-100">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mb-4">
            {t.currentState.title}
          </h2>
          <p className="text-stone-500 text-lg">
            {t.currentState.subtitle}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {displayImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => openLightbox(index)}
              className="relative overflow-hidden group cursor-pointer aspect-square"
            >
              <img
                src={image.url}
                alt={image.alt[language]}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 grayscale-[30%]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
            </button>
          ))}
        </div>

        {/* View More Button */}
        {currentStateImages.length > 6 && (
          <div className="text-center mt-8">
            <button
              onClick={() => openLightbox(0)}
              className="text-stone-600 hover:text-stone-800 text-sm tracking-wider uppercase transition-colors"
            >
              {language === 'es' ? `Ver todas las fotos (${currentStateImages.length})` : `View all photos (${currentStateImages.length})`}
            </button>
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-5xl w-full bg-black/95 border-none p-0">
            <div className="relative w-full aspect-[4/3]">
              {selectedImage && (
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt[language]}
                  className="w-full h-full object-contain"
                />
              )}
              
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>

              {/* Navigation */}
              <button
                onClick={goToPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-2"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-2"
              >
                <ChevronRight size={32} />
              </button>

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                {currentIndex + 1} / {currentStateImages.length}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default CurrentStateGallery;
